import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import latinize from 'latinize';
import Select from 'react-select';
import debounce from 'lodash.debounce';
import clubMapping from '../../data/clupMapping.json';
import achievementsData from '../../data/achievements.json';
import Navbar from '../../components/Navbar/Navbar';
import Title from '../../components/Title/Title';
import './ClubRacePage.css';

const backendURL = process.env.REACT_APP_BACKEND_URL || 'https://api.footy-games.com.ar';

const ITEMS_PER_ROUND = 4;

const ACHIEVEMENT_LABELS = {
  champions: 'Champions League',
  balon_oro: 'Balón de Oro',
  campeon_mundo: 'Campeón del Mundo',
  europa_league: 'Europa League',
  eurocopa: 'Eurocopa',
  copa_america: 'Copa América',
  jjoo: 'JJOO',
  bota_oro: 'Bota de Oro',
  '60transferValue': 'Valor > 60M',
  goldenboy: 'Golden Boy',
  premier_league: 'Premier League',
  puskas: 'Premio Puskás',
  conference_league: 'Conference League',
  triplete: 'Triplete',
  nations_league: 'Nations League',
};

const EXCLUDED_ACHIEVEMENTS = new Set(['african_player', 'concacaf_player']);

const ALL_CLUBS = Object.entries(clubMapping).map(([label, value]) => ({ label, value, type: 'club' }));
const ALL_ACHIEVEMENTS = Object.keys(achievementsData)
  .filter(key => !EXCLUDED_ACHIEVEMENTS.has(key))
  .map(key => ({
    label: ACHIEVEMENT_LABELS[key] || key,
    value: key,
    type: 'achievement',
  }));

const TOP_FOOTBALL_COUNTRIES = [
  'Argentina', 'Brasil', 'España', 'Francia', 'Alemania', 'Italia', 'Portugal',
  'Inglaterra', 'Paises Bajos', 'Belgica', 'Uruguay', 'Colombia', 'Mexico',
  'Croacia', 'Senegal', 'Marruecos', 'Ghana', 'Nigeria', 'Estados Unidos',
  'Japon', 'Corea del Sur', 'Serbia', 'Suiza', 'Dinamarca', 'Austria',
].map(c => ({ label: c, value: c, type: 'country' }));

const ALL_COUNTRIES = TOP_FOOTBALL_COUNTRIES;

/* ========== BOT PERSONALITIES ========== */
const BOT_PERSONALITIES = [
  {
    name: 'Carlos',
    country: 'Argentina',
    flag: '🇦🇷',
    color: '#75AADB',
    jersey: '#75AADB',
    comments: {
      thinking: ['Espera que pienso como Riquelme...', 'Dejame pensar con mate en mano...', 'Como diria el Diego...', 'Toco y me voy...'],
      goodPick: ['¡Golazo! Mejor que Messi', '¿Vieron? Soy un crack', 'La tengo clara como Roman', 'De la cuna al gol'],
      badPick: ['Culpa del arbitro seguro', 'Me colgue mirando el asado', 'Bueno, no se puede ganar siempre', 'Le erro como Higuain'],
      winning: ['¡Vamos carajo!', '¿Quien tiene 3 mundiales?', 'Dale campeon dale campeon', 'Muchachos, ahora nos volvimos a ilusionar'],
      losing: ['Esto es peor que Islandia 2018', 'Recien empieza esto', 'Esperame que ya arranco', 'La pelota no dobla'],
      timeout: ['Se te fue el bondi', 'Dormiste como Caballero', 'Se te paso el mate', 'Mas dormido que siesta en Rosario'],
    },
  },
  {
    name: 'Pierre',
    country: 'Francia',
    flag: '🇫🇷',
    color: '#002395',
    jersey: '#002395',
    comments: {
      thinking: ['Un moment, s\'il vous plait...', 'Comme Zidane pensait...', 'Analizando con estilo frances...', 'Avec elegance...'],
      goodPick: ['Magnifique!', 'C\'est la classe', 'Mejor que un croissant', 'Tres bien joue'],
      badPick: ['C\'est la vie...', 'Sacre bleu!', 'No importa, tenemos a Mbappe', 'Pas grave'],
      winning: ['Allez les Bleus!', 'La France en tete!', 'Formidable!', 'On est les champions'],
      losing: ['On va revenir!', 'Ce n\'est pas fini', 'Un partido no se gana hasta el final', 'Mon dieu...'],
      timeout: ['Tu as dormi!', 'Pas de temps!', 'El tiempo no perdona', 'Muy lento, mon ami'],
    },
  },
  {
    name: 'Hans',
    country: 'Alemania',
    flag: '🇩🇪',
    color: '#DD0000',
    jersey: '#DD0000',
    comments: {
      thinking: ['Analizando con precision alemana...', 'Eficiencia ante todo...', 'Wie Beckenbauer sagt...', 'Calculando probabilidades...'],
      goodPick: ['Wunderbar!', 'Eficiencia pura', 'Puntual y preciso', 'Genau so!'],
      badPick: ['Das ist nicht gut...', 'Necesito mas datos', 'Error de calculo', 'Ach, Mist!'],
      winning: ['Somos maquina!', 'Sin errores', 'Precision alemana', 'Jawohl!'],
      losing: ['Hay que replantear la tactica', 'El plan necesita ajustes', 'Recalibrando...', 'Nur die Ruhe...'],
      timeout: ['Keine Zeit!', 'Se te acabo el tiempo', 'Los alemanes somos puntuales', 'Demasiado lento'],
    },
  },
];

const getRandomComment = (botIdx, category) => {
  const bot = BOT_PERSONALITIES[botIdx - 1];
  if (!bot || !bot.comments[category]) return '';
  const arr = bot.comments[category];
  return arr[Math.floor(Math.random() * arr.length)];
};

const getBotDisplayName = (playerIdx) => {
  if (playerIdx === 0) return 'Tu';
  const bot = BOT_PERSONALITIES[playerIdx - 1];
  return bot ? `${bot.flag} ${bot.name}` : `Bot ${playerIdx}`;
};

const getBotShortName = (playerIdx) => {
  if (playerIdx === 0) return 'Tu';
  const bot = BOT_PERSONALITIES[playerIdx - 1];
  return bot ? bot.name : `Bot ${playerIdx}`;
};

const getAvatarContent = (playerIdx) => {
  if (playerIdx === 0) return 'T';
  const bot = BOT_PERSONALITIES[playerIdx - 1];
  return bot ? bot.flag : 'B';
};

const getAvatarStyle = (playerIdx) => {
  if (playerIdx === 0) return {};
  const bot = BOT_PERSONALITIES[playerIdx - 1];
  if (!bot) return {};
  return { background: `linear-gradient(135deg, ${bot.color}, ${bot.color}88)` };
};

/* ========== SCORING ========== */
const scorePlayerAgainstCriteria = (player, criteria) => {
  let score = 0;
  const matches = [];
  criteria.forEach(item => {
    if (item.type === 'club' && player.clubes?.includes(item.value)) {
      score++;
      matches.push(item.label);
    } else if (item.type === 'achievement') {
      if (item.value === '60transferValue') {
        if (player.maxTransferValue >= 60) {
          score++;
          matches.push(item.label);
        }
      } else if (player[item.value]) {
        score++;
        matches.push(item.label);
      }
    } else if (item.type === 'country' && player.pais === item.value) {
      score++;
      matches.push(item.label);
    }
  });
  return { score, matches };
};

const getBirthYear = (fechaNacimiento) => {
  if (!fechaNacimiento) return null;
  const parts = fechaNacimiento.split('.');
  return parts.length === 3 ? parts[2] : null;
};

const ClubRacePage = () => {
  const navigate = useNavigate();

  // Settings
  const [pointsGoal, setPointsGoal] = useState(15);
  const [difficulty, setDifficulty] = useState('semipro');
  const [timeLimit, setTimeLimit] = useState(30);

  // Game state
  const [phase, setPhase] = useState('setup');
  const [turn, setTurn] = useState(0);
  const [round, setRound] = useState(1);
  const [scores, setScores] = useState([0, 0, 0, 0]);
  const [chosenItems, setChosenItems] = useState([]);
  const [chosenPlayers, setChosenPlayers] = useState([]);
  const [available, setAvailable] = useState([]);
  const [countryPickedThisRound, setCountryPickedThisRound] = useState(false);
  const [activeTab, setActiveTab] = useState('clubs');
  const [countrySearch, setCountrySearch] = useState('');
  const [botThinking, setBotThinking] = useState(false);
  const [animatingResult, setAnimatingResult] = useState(-1);
  const [winner, setWinner] = useState(null);
  const [bestPlayers, setBestPlayers] = useState([]);
  const [loadingBest, setLoadingBest] = useState(false);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Bot comment state
  const [botComment, setBotComment] = useState({ botIdx: -1, text: '', visible: false });

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Refs
  const botTimerRef = useRef(null);
  const phaseRef = useRef(phase);
  const turnRef = useRef(turn);
  const chosenItemsRef = useRef(chosenItems);
  const chosenPlayersRef = useRef([]);
  const usedPlayersRef = useRef(new Set());
  const difficultyRef = useRef(difficulty);
  const countryPickedRef = useRef(false);
  const availableRef = useRef(available);
  const roundStartPlayerRef = useRef(0);
  const itemsPickedRef = useRef(0);
  const playersPickedRef = useRef(0);
  const globalUsedItemsRef = useRef(new Set());
  const timerIntervalRef = useRef(null);
  const botCommentTimerRef = useRef(null);
  const timeLimitRef = useRef(timeLimit);

  const roundStartPlayer = (round - 1) % 4;

  useEffect(() => { phaseRef.current = phase; }, [phase]);
  useEffect(() => { turnRef.current = turn; }, [turn]);
  useEffect(() => { chosenItemsRef.current = chosenItems; }, [chosenItems]);
  useEffect(() => { chosenPlayersRef.current = chosenPlayers; }, [chosenPlayers]);
  useEffect(() => { difficultyRef.current = difficulty; }, [difficulty]);
  useEffect(() => { countryPickedRef.current = countryPickedThisRound; }, [countryPickedThisRound]);
  useEffect(() => { availableRef.current = available; }, [available]);
  useEffect(() => { roundStartPlayerRef.current = roundStartPlayer; }, [roundStartPlayer]);
  useEffect(() => { timeLimitRef.current = timeLimit; }, [timeLimit]);

  useEffect(() => {
    return () => {
      if (botTimerRef.current) clearTimeout(botTimerRef.current);
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (botCommentTimerRef.current) clearTimeout(botCommentTimerRef.current);
    };
  }, []);

  // Show bot comment temporarily
  const showBotComment = useCallback((botIdx, text) => {
    if (botCommentTimerRef.current) clearTimeout(botCommentTimerRef.current);
    setBotComment({ botIdx, text, visible: true });
    botCommentTimerRef.current = setTimeout(() => {
      setBotComment(prev => ({ ...prev, visible: false }));
    }, 3000);
  }, []);

  /* ========== TIMER ========== */
  const handleTimerExpired = useCallback(() => {
    if (phaseRef.current === 'items' && turnRef.current === 0) {
      const pool = availableRef.current;
      if (pool.length === 0) return;
      const randomItem = pool[Math.floor(Math.random() * pool.length)];
      setChosenItems(prev => [...prev, { playerIdx: 0, type: randomItem.type, value: randomItem.value, label: randomItem.label }]);
      setAvailable(prev => prev.filter(i => !(i.type === randomItem.type && i.value === randomItem.value)));
      globalUsedItemsRef.current.add(randomItem.type + ':' + randomItem.value);
      if (randomItem.type === 'country') {
        setCountryPickedThisRound(true);
        countryPickedRef.current = true;
      }
      itemsPickedRef.current += 1;
      if (itemsPickedRef.current >= ITEMS_PER_ROUND) {
        setPhase('players');
        setTurn(roundStartPlayerRef.current);
      } else {
        setTurn((turnRef.current + 1) % 4);
      }
      // Random bot comments about timeout
      const randomBot = 1 + Math.floor(Math.random() * 3);
      showBotComment(randomBot, getRandomComment(randomBot, 'timeout'));
    } else if (phaseRef.current === 'players' && turnRef.current === 0) {
      setChosenPlayers(prev => [...prev, { playerIdx: 0, name: 'Sin resultado (tiempo)', points: 0, matchingCriteria: [] }]);
      playersPickedRef.current += 1;
      if (playersPickedRef.current >= ITEMS_PER_ROUND) {
        setPhase('results');
      } else {
        setTurn((turnRef.current + 1) % 4);
      }
      setSearchInput('');
      setSearchResults([]);
      const randomBot = 1 + Math.floor(Math.random() * 3);
      showBotComment(randomBot, getRandomComment(randomBot, 'timeout'));
    }
  }, [showBotComment]);

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
    if (timeLimitRef.current === 0 || turn !== 0 || (phase !== 'items' && phase !== 'players')) {
      setTimeRemaining(null);
      return;
    }
    const limit = timeLimitRef.current;
    setTimeRemaining(limit);
    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setTimeout(() => handleTimerExpired(), 0);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [turn, phase, timeLimit, handleTimerExpired]);

  /* ========== GAME FUNCTIONS ========== */
  const startGame = () => {
    usedPlayersRef.current = new Set();
    chosenPlayersRef.current = [];
    chosenItemsRef.current = [];
    itemsPickedRef.current = 0;
    playersPickedRef.current = 0;
    globalUsedItemsRef.current = new Set();
    if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
    setTimeRemaining(null);
    setBotComment({ botIdx: -1, text: '', visible: false });
    const allItems = [...ALL_CLUBS, ...ALL_ACHIEVEMENTS, ...ALL_COUNTRIES];
    setAvailable(allItems);
    setCountryPickedThisRound(false);
    countryPickedRef.current = false;
    setChosenItems([]);
    setChosenPlayers([]);
    setScores([0, 0, 0, 0]);
    setRound(1);
    setTurn(0);
    setPhase('items');
    setActiveTab('clubs');
    setCountrySearch('');
  };

  const botPickItem = useCallback((currentAvailable, countryAlreadyPicked) => {
    let pool = currentAvailable;
    if (countryAlreadyPicked) {
      pool = pool.filter(i => i.type !== 'country');
    }
    if (pool.length === 0) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  }, []);

  const botPickPlayer = useCallback(async (criteria, currentDifficulty) => {
    try {
      const clubs = criteria.filter(c => c.type === 'club').map(c => c.value).join(',');
      const achievements = criteria.filter(c => c.type === 'achievement').map(c => c.value).join(',');
      const country = criteria.find(c => c.type === 'country')?.value || '';
      const params = new URLSearchParams();
      if (clubs) params.set('clubs', clubs);
      if (achievements) params.set('achievements', achievements);
      if (country) params.set('country', country);
      params.set('league', 'normal');
      params.set('limit', '100');

      const res = await fetch(`${backendURL}/players/searchByCriteriaMixed?${params}`);
      const players = await res.json();
      const botIdx = turnRef.current;
      const usedNames = usedPlayersRef.current;

      if (!players || players.length === 0) {
        setChosenPlayers(prev => [...prev, { playerIdx: botIdx, name: 'Sin resultado', points: 0, matchingCriteria: [] }]);
        playersPickedRef.current += 1;
        if (playersPickedRef.current >= ITEMS_PER_ROUND) {
          setPhase('results');
        } else {
          setTurn((turnRef.current + 1) % 4);
        }
        showBotComment(botIdx, getRandomComment(botIdx, 'badPick'));
      } else {
        const available_players = players.filter(p => !usedNames.has(p.nombre));
        const pool = available_players.length > 0 ? available_players : players;

        let finalPick;
        const roll = Math.random();

        if (currentDifficulty === 'juvenil') {
          if (roll < 0.45 || pool.length <= 3) {
            finalPick = pool[Math.floor(Math.random() * pool.length)];
          } else if (roll < 0.70) {
            const start = Math.floor(pool.length * 0.5);
            const slice = pool.slice(start);
            finalPick = slice[Math.floor(Math.random() * slice.length)];
          } else {
            const top40 = Math.max(1, Math.ceil(pool.length * 0.4));
            finalPick = pool[Math.floor(Math.random() * top40)];
          }
        } else if (currentDifficulty === 'amateur') {
          if (roll < 0.40) {
            const top35 = Math.max(1, Math.ceil(pool.length * 0.35));
            finalPick = pool[Math.floor(Math.random() * top35)];
          } else if (roll < 0.75) {
            const top15 = Math.max(1, Math.ceil(pool.length * 0.15));
            finalPick = pool[Math.floor(Math.random() * top15)];
          } else {
            finalPick = pool[Math.floor(Math.random() * pool.length)];
          }
        } else if (currentDifficulty === 'semipro') {
          if (roll < 0.45) {
            const top15 = Math.max(1, Math.ceil(pool.length * 0.15));
            finalPick = pool[Math.floor(Math.random() * top15)];
          } else if (roll < 0.80) {
            const top8 = Math.max(1, Math.ceil(pool.length * 0.08));
            finalPick = pool[Math.floor(Math.random() * top8)];
          } else {
            const top30 = Math.max(1, Math.ceil(pool.length * 0.3));
            finalPick = pool[Math.floor(Math.random() * top30)];
          }
        } else if (currentDifficulty === 'profesional') {
          if (roll < 0.50) {
            const top3 = Math.max(1, Math.min(5, Math.ceil(pool.length * 0.03)));
            finalPick = pool[Math.floor(Math.random() * top3)];
          } else if (roll < 0.80) {
            const top8 = Math.max(1, Math.ceil(pool.length * 0.08));
            finalPick = pool[Math.floor(Math.random() * top8)];
          } else {
            const top20 = Math.max(1, Math.ceil(pool.length * 0.2));
            finalPick = pool[Math.floor(Math.random() * top20)];
          }
        } else {
          if (roll < 0.80) {
            finalPick = pool[0];
          } else if (roll < 0.92) {
            const top3 = Math.min(3, pool.length);
            finalPick = pool[Math.floor(Math.random() * top3)];
          } else {
            const top5 = Math.min(5, pool.length);
            finalPick = pool[Math.floor(Math.random() * top5)];
          }
        }

        usedPlayersRef.current.add(finalPick.nombre);
        const newEntry = {
          playerIdx: botIdx,
          name: finalPick.nombre,
          points: finalPick.matchingCriteriaCount,
          matchingCriteria: finalPick.matchingCriteria || [],
        };
        setChosenPlayers(prev => [...prev, newEntry]);
        playersPickedRef.current += 1;
        if (playersPickedRef.current >= ITEMS_PER_ROUND) {
          setPhase('results');
        } else {
          setTurn((turnRef.current + 1) % 4);
        }
        const commentType = finalPick.matchingCriteriaCount >= 3 ? 'goodPick' : finalPick.matchingCriteriaCount <= 1 ? 'badPick' : 'goodPick';
        showBotComment(botIdx, getRandomComment(botIdx, commentType));
      }
    } catch (err) {
      console.error('Bot player selection error:', err);
      const botIdx = turnRef.current;
      setChosenPlayers(prev => [...prev, { playerIdx: botIdx, name: 'Error', points: 0, matchingCriteria: [] }]);
      playersPickedRef.current += 1;
      if (playersPickedRef.current >= ITEMS_PER_ROUND) {
        setPhase('results');
      } else {
        setTurn((turnRef.current + 1) % 4);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBotComment]);

  // Handle bot turns
  useEffect(() => {
    if (phase === 'setup' || phase === 'results' || phase === 'gameover') return;
    if (turn === 0) return;

    if (phase === 'items') {
      setBotThinking(true);
      showBotComment(turn, getRandomComment(turn, 'thinking'));
      const delay = 800 + Math.random() * 1500;
      botTimerRef.current = setTimeout(() => {
        const pick = botPickItem(availableRef.current, countryPickedRef.current);
        if (!pick) { setBotThinking(false); return; }
        const botIdx = turnRef.current;
        setChosenItems(prev => [...prev, { playerIdx: botIdx, type: pick.type, value: pick.value, label: pick.label }]);
        setAvailable(prev => prev.filter(i => !(i.type === pick.type && i.value === pick.value)));
        if (pick.type === 'country') {
          setCountryPickedThisRound(true);
          countryPickedRef.current = true;
        }
        itemsPickedRef.current += 1;
        if (itemsPickedRef.current >= ITEMS_PER_ROUND) {
          setPhase('players');
          setTurn(roundStartPlayerRef.current);
        } else {
          setTurn((turnRef.current + 1) % 4);
        }
        showBotComment(botIdx, getRandomComment(botIdx, 'itemPick'));
        setBotThinking(false);
      }, delay);
    }

    if (phase === 'players') {
      setBotThinking(true);
      showBotComment(turn, getRandomComment(turn, 'thinking'));
      const delay = 1200 + Math.random() * 2000;
      botTimerRef.current = setTimeout(async () => {
        await botPickPlayer(chosenItemsRef.current, difficultyRef.current);
        setBotThinking(false);
      }, delay);
    }

    return () => { if (botTimerRef.current) clearTimeout(botTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [turn, phase]);

  // Animate results and update scores
  useEffect(() => {
    if (phase === 'results') {
      const sorted = [...chosenPlayers].sort((a, b) => b.points - a.points);
      let i = 0;
      const interval = setInterval(() => {
        setAnimatingResult(i);
        i++;
        if (i >= sorted.length) clearInterval(interval);
      }, 600);

      setScores(prev => {
        const next = [...prev];
        chosenPlayers.forEach(p => { next[p.playerIdx] += p.points; });
        const reachedGoal = next.map((s, idx) => ({ s, i: idx })).filter(x => x.s >= pointsGoal);
        if (reachedGoal.length > 0) {
          const topWinner = reachedGoal.reduce((best, curr) => curr.s > best.s ? curr : best);
          setWinner(topWinner.i);
        }
        return next;
      });

      return () => clearInterval(interval);
    }
    setAnimatingResult(-1);
  }, [phase, chosenPlayers, pointsGoal]);

  // User picks an item
  const handleItemPick = (item) => {
    setChosenItems(prev => [...prev, { playerIdx: 0, type: item.type, value: item.value, label: item.label }]);
    setAvailable(prev => prev.filter(i => !(i.type === item.type && i.value === item.value)));
    globalUsedItemsRef.current.add(item.type + ':' + item.value);
    if (item.type === 'country') {
      setCountryPickedThisRound(true);
      countryPickedRef.current = true;
    }
    itemsPickedRef.current += 1;
    if (itemsPickedRef.current >= ITEMS_PER_ROUND) {
      setPhase('players');
      setTurn(roundStartPlayer);
    } else {
      setTurn((turn + 1) % 4);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce(async (term, criteria) => {
      if (term.length < 3) { setSearchResults([]); setIsSearching(false); return; }
      setIsSearching(true);
      try {
        const normalized = latinize(term);
        const res = await fetch(`${backendURL}/players/search?search=${normalized}`);
        const allPlayers = await res.json();
        const scored = allPlayers.map(p => {
          const { score, matches } = scorePlayerAgainstCriteria(p, criteria);
          return { ...p, matchingCriteriaCount: score, matchingCriteria: matches };
        }).filter(p => p.matchingCriteriaCount > 0 && !usedPlayersRef.current.has(p.nombre))
          .sort((a, b) => b.matchingCriteriaCount - a.matchingCriteriaCount);
        setSearchResults(scored.slice(0, 40));
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 400),
    []
  );

  useEffect(() => {
    if (searchInput.length >= 3) {
      debouncedSearch(searchInput, chosenItems);
    } else {
      setSearchResults([]);
    }
    return () => debouncedSearch.cancel();
  }, [searchInput, chosenItems, debouncedSearch]);

  const handlePlayerPick = (option) => {
    const player = option.value;
    usedPlayersRef.current.add(player.nombre);
    const newEntry = {
      playerIdx: 0,
      name: player.nombre,
      points: player.matchingCriteriaCount,
      matchingCriteria: player.matchingCriteria,
    };
    setChosenPlayers(prev => [...prev, newEntry]);
    playersPickedRef.current += 1;
    if (playersPickedRef.current >= ITEMS_PER_ROUND) {
      setPhase('results');
    } else {
      setTurn((turn + 1) % 4);
    }
    setSearchInput('');
    setSearchResults([]);
  };

  const startNextRound = () => {
    if (winner !== null) {
      setPhase('gameover');
      return;
    }
    usedPlayersRef.current = new Set();
    chosenPlayersRef.current = [];
    chosenItemsRef.current = [];
    itemsPickedRef.current = 0;
    playersPickedRef.current = 0;
    const newRound = round + 1;
    const newStart = (newRound - 1) % 4;
    setChosenItems([]);
    setChosenPlayers([]);
    setPhase('items');
    setTurn(newStart);
    setRound(newRound);
    setAvailable(
      [...ALL_CLUBS, ...ALL_ACHIEVEMENTS, ...ALL_COUNTRIES]
        .filter(i => !globalUsedItemsRef.current.has(i.type + ':' + i.value))
    );
    setCountryPickedThisRound(false);
    countryPickedRef.current = false;
    setSearchInput('');
    setSearchResults([]);
    setActiveTab('clubs');
    setCountrySearch('');
    setAnimatingResult(-1);
    setWinner(null);
    setBestPlayers([]);
    setBotComment({ botIdx: -1, text: '', visible: false });
  };

  // Auto-fetch best players when results phase starts
  useEffect(() => {
    if (phase !== 'results') return;
    const items = chosenItemsRef.current;
    const clubs = items.filter(c => c.type === 'club').map(c => c.value).join(',');
    const achievements = items.filter(c => c.type === 'achievement').map(c => c.value).join(',');
    const country = items.find(c => c.type === 'country')?.value || '';
    const params = new URLSearchParams();
    if (clubs) params.set('clubs', clubs);
    if (achievements) params.set('achievements', achievements);
    if (country) params.set('country', country);
    params.set('league', 'normal');
    params.set('limit', '5');
    setLoadingBest(true);
    setBestPlayers([]);
    fetch(`${backendURL}/players/searchByCriteriaMixed?${params}`)
      .then(res => res.json())
      .then(players => setBestPlayers(players))
      .catch(err => console.error('Error fetching best players:', err))
      .finally(() => setLoadingBest(false));
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetGame = () => {
    setPhase('setup');
    setScores([0, 0, 0, 0]);
    setRound(1);
    setWinner(null);
    usedPlayersRef.current = new Set();
    globalUsedItemsRef.current = new Set();
    if (timerIntervalRef.current) { clearInterval(timerIntervalRef.current); timerIntervalRef.current = null; }
    setTimeRemaining(null);
    setBotComment({ botIdx: -1, text: '', visible: false });
  };

  const formatOptions = (players) =>
    players.map(p => ({
      value: p,
      label: (
        <div className="cr-option">
          {p.img_pais && <img src={p.img_pais} alt="" className="cr-option-flag" />}
          <span className="cr-option-name">{p.nombre}</span>
          <span className="cr-option-pos">{p.posicion}</span>
          {getBirthYear(p.fecha_nacimiento) && (
            <span className="cr-option-year">{getBirthYear(p.fecha_nacimiento)}</span>
          )}
        </div>
      ),
    }));

  const sortedResults = [...chosenPlayers].sort((a, b) => b.points - a.points);
  const rankedScores = scores
    .map((s, i) => ({ name: getBotDisplayName(i), score: s, idx: i }))
    .sort((a, b) => b.score - a.score);

  const isUserTurn = turn === 0 && !botThinking;

  const availableClubs = available.filter(i => i.type === 'club');
  const availableAchievements = available.filter(i => i.type === 'achievement');
  const availableCountries = available.filter(i => i.type === 'country');
  const filteredCountries = countrySearch.length > 0
    ? availableCountries.filter(c => latinize(c.label.toLowerCase()).includes(latinize(countrySearch.toLowerCase())))
    : availableCountries;

  const getCriteriaLabel = (item) => {
    if (item.type === 'country') return `🌍 ${item.label}`;
    return item.label;
  };

  // Pre-compute result reactions so they don't change on re-render
  const resultReactions = useMemo(() => {
    if (phase !== 'results') return [];
    return [1, 2, 3].map(botIdx => {
      const botRank = rankedScores.findIndex(s => s.idx === botIdx);
      const commentType = botRank === 0 ? 'winning' : botRank >= 2 ? 'losing' : 'goodPick';
      return { botIdx, text: getRandomComment(botIdx, commentType) };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, round]);

  /* ========== TIMER BAR COMPONENT ========== */
  const TimerBar = () => {
    if (timeRemaining === null || turn !== 0 || timeLimit === 0) return null;
    return (
      <div className="cr-timer-container">
        <div className="cr-timer-bar-bg">
          <div
            className={`cr-timer-bar ${timeRemaining <= 5 ? 'danger' : timeRemaining <= 10 ? 'warning' : ''}`}
            style={{ width: `${(timeRemaining / timeLimit) * 100}%` }}
          />
        </div>
        <span className={`cr-timer-text ${timeRemaining <= 5 ? 'danger' : timeRemaining <= 10 ? 'warning' : ''}`}>
          {timeRemaining}s
        </span>
      </div>
    );
  };

  /* ========== SPEECH BUBBLE ========== */
  const SpeechBubble = () => {
    if (!botComment.visible || botComment.botIdx <= 0) return null;
    const bot = BOT_PERSONALITIES[botComment.botIdx - 1];
    if (!bot) return null;
    return (
      <div className="cr-speech-bubble cr-slide-in">
        <span className="cr-speech-flag">{bot.flag}</span>
        <span className="cr-speech-name">{bot.name}:</span>
        <span className="cr-speech-text">{botComment.text}</span>
      </div>
    );
  };

  return (
    <div className="cr-page">
      <ToastContainer position="top-center" autoClose={2500} theme="dark" />
      <Navbar />
      <Title onClick={() => navigate('/')} />

      {/* =============== PHASE: SETUP =============== */}
      {phase === 'setup' && (
        <div className="cr-setup">
          <div className="cr-setup-card">
            <h2>Club Race</h2>
            <p className="cr-setup-desc">
              Cada jugador elige un criterio (club, logro o pais). Luego hay que nombrar al futbolista que cumpla mas criterios.
            </p>

            <div className="cr-setup-group">
              <label className="cr-setup-label">Objetivo de puntos</label>
              <div className="cr-setup-opts">
                {[10, 15, 20, 25, 30, 50].map(v => (
                  <button
                    key={v}
                    className={`cr-setup-opt ${pointsGoal === v ? 'selected' : ''}`}
                    onClick={() => setPointsGoal(v)}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <div className="cr-setup-group">
              <label className="cr-setup-label">Tiempo por turno</label>
              <div className="cr-setup-opts">
                {[
                  { value: 0, label: 'Sin tiempo' },
                  { value: 7, label: '7s' },
                  { value: 10, label: '10s' },
                  { value: 15, label: '15s' },
                  { value: 20, label: '20s' },
                  { value: 30, label: '30s' },
                  { value: 45, label: '45s' },
                  { value: 60, label: '60s' },
                ].map(t => (
                  <button
                    key={t.value}
                    className={`cr-setup-opt ${timeLimit === t.value ? 'selected' : ''}`}
                    onClick={() => setTimeLimit(t.value)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="cr-setup-group">
              <label className="cr-setup-label">Dificultad del bot</label>
              <div className="cr-difficulty-levels">
                {[
                  { value: 'juvenil', label: 'Juvenil', desc: 'Recien empieza, bastante perdido', icon: '🌱' },
                  { value: 'amateur', label: 'Amateur', desc: 'Sabe algo, a veces sorprende', icon: '⚽' },
                  { value: 'semipro', label: 'Semi-Pro', desc: 'Competitivo y parejo', icon: '🎯' },
                  { value: 'profesional', label: 'Profesional', desc: 'Conoce bien, dificil ganarle', icon: '🏟️' },
                  { value: 'leyenda', label: 'Leyenda', desc: 'Enciclopedia futbolera', icon: '👑' },
                ].map(d => (
                  <button
                    key={d.value}
                    className={`cr-diff-card ${difficulty === d.value ? 'selected' : ''}`}
                    onClick={() => setDifficulty(d.value)}
                  >
                    <span className="cr-diff-icon">{d.icon}</span>
                    <span className="cr-diff-label">{d.label}</span>
                    <span className="cr-diff-desc">{d.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Bot preview */}
            <div className="cr-bot-preview">
              <label className="cr-setup-label">Tus rivales</label>
              <div className="cr-bot-preview-row">
                {BOT_PERSONALITIES.map((bot, i) => (
                  <div key={i} className="cr-bot-card-preview">
                    <div className="cr-bot-avatar-big" style={{ background: `linear-gradient(135deg, ${bot.color}, ${bot.color}88)` }}>
                      <span className="cr-bot-avatar-flag">{bot.flag}</span>
                      <span className="cr-bot-avatar-icon">🤖</span>
                    </div>
                    <span className="cr-bot-card-name">{bot.name}</span>
                    <span className="cr-bot-card-country">{bot.country}</span>
                  </div>
                ))}
              </div>
            </div>

            <button className="cr-start-btn" onClick={startGame}>
              Empezar
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      {phase !== 'setup' && (
        <header className="cr-header">
          <div className="cr-header-left">
            <span className="cr-round-badge">Ronda {round}</span>
            <div className="cr-phase-indicator">
              <div className={`cr-phase-dot ${phase === 'items' ? 'active' : phase !== 'items' ? 'done' : ''}`}>1</div>
              <div className="cr-phase-line" />
              <div className={`cr-phase-dot ${phase === 'players' ? 'active' : phase === 'results' || phase === 'gameover' ? 'done' : ''}`}>2</div>
              <div className="cr-phase-line" />
              <div className={`cr-phase-dot ${phase === 'results' || phase === 'gameover' ? 'active' : ''}`}>3</div>
            </div>
            <span className="cr-goal-badge">Meta: {pointsGoal} pts</span>
          </div>
          <div className="cr-scoreboard">
            {[0, 1, 2, 3].map(i => {
              const bot = i > 0 ? BOT_PERSONALITIES[i - 1] : null;
              return (
                <div
                  key={i}
                  className={`cr-score-card ${turn === i && phase !== 'results' && phase !== 'gameover' ? 'current' : ''} ${scores[i] >= pointsGoal ? 'winner-card' : ''}`}
                  style={bot && turn === i ? { borderColor: bot.color } : {}}
                >
                  {bot && <span className="cr-score-flag">{bot.flag}</span>}
                  <span className="cr-score-name">{i === 0 ? 'Tu' : bot.name}</span>
                  <span className="cr-score-pts">{scores[i]}</span>
                </div>
              );
            })}
          </div>
        </header>
      )}

      <main className="cr-main">
        {/* =============== PHASE: ITEMS =============== */}
        {phase === 'items' && (
          <section className="cr-section cr-fade-in">
            <div className="cr-section-header">
              <h2>Elegir Criterios</h2>
              <p>Cada jugador elige un club, logro o pais. Luego hay que nombrar al futbolista que cumpla mas de ellos.</p>
              {round > 1 && (
                <div className="cr-starts-badge">
                  Empieza: <strong>{getBotDisplayName(roundStartPlayer)}</strong>
                </div>
              )}
            </div>

            <div className="cr-turn-banner">
              {botThinking ? (
                <>
                  <div className="cr-dots"><span /><span /><span /></div>
                  <span>{getBotDisplayName(turn)} pensando...</span>
                </>
              ) : (
                <span>{turn === 0 ? 'Tu turno: elegi un criterio' : `Turno de ${getBotDisplayName(turn)}`}</span>
              )}
            </div>

            <SpeechBubble />
            <TimerBar />

            <div className="cr-timeline">
              {chosenItems.map((c, i) => (
                <div key={i} className="cr-timeline-item cr-slide-in" style={{ animationDelay: `${i * 0.1}s` }}>
                  <div className={`cr-timeline-avatar ${c.playerIdx === 0 ? 'user' : 'bot'}`} style={getAvatarStyle(c.playerIdx)}>
                    {getAvatarContent(c.playerIdx)}
                  </div>
                  <div className="cr-timeline-content">
                    <span className="cr-timeline-player">{getBotShortName(c.playerIdx)}</span>
                    <span className={`cr-timeline-club cr-type-${c.type}`}>{getCriteriaLabel(c)}</span>
                  </div>
                </div>
              ))}
              {chosenItems.length < ITEMS_PER_ROUND && (
                <div className="cr-timeline-item pending">
                  <div className="cr-timeline-avatar pending-avatar">?</div>
                  <div className="cr-timeline-content">
                    <span className="cr-timeline-player">{getBotShortName(turn)}</span>
                    <span className="cr-timeline-club faded">Esperando...</span>
                  </div>
                </div>
              )}
            </div>

            {isUserTurn && (
              <div className="cr-picker">
                <div className="cr-tabs">
                  <button className={`cr-tab ${activeTab === 'clubs' ? 'active' : ''}`} onClick={() => setActiveTab('clubs')}>
                    Clubes ({availableClubs.length})
                  </button>
                  <button className={`cr-tab ${activeTab === 'achievements' ? 'active' : ''}`} onClick={() => setActiveTab('achievements')}>
                    Logros ({availableAchievements.length})
                  </button>
                  <button
                    className={`cr-tab ${activeTab === 'country' ? 'active' : ''} ${countryPickedThisRound ? 'disabled-tab' : ''}`}
                    onClick={() => !countryPickedThisRound && setActiveTab('country')}
                    disabled={countryPickedThisRound}
                  >
                    Pais {countryPickedThisRound ? '(ya elegido)' : ''}
                  </button>
                </div>

                {activeTab === 'clubs' && (
                  <div className="cr-club-grid">
                    {availableClubs.map(club => (
                      <button key={club.value} className="cr-club-btn" onClick={() => handleItemPick(club)}>
                        {club.label}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'achievements' && (
                  <div className="cr-achievement-grid">
                    {availableAchievements.map(ach => (
                      <button key={ach.value} className="cr-achievement-btn" onClick={() => handleItemPick(ach)}>
                        {ach.label}
                      </button>
                    ))}
                  </div>
                )}

                {activeTab === 'country' && !countryPickedThisRound && (
                  <div className="cr-country-picker">
                    <input
                      type="text"
                      className="cr-search-input"
                      placeholder="Buscar pais..."
                      value={countrySearch}
                      onChange={e => setCountrySearch(e.target.value)}
                      autoFocus
                    />
                    <div className="cr-country-grid">
                      {filteredCountries.map(country => (
                        <button key={country.value} className="cr-country-btn" onClick={() => { handleItemPick(country); setCountrySearch(''); }}>
                          {country.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </section>
        )}

        {/* =============== PHASE: PLAYERS =============== */}
        {phase === 'players' && (
          <section className="cr-section cr-fade-in">
            <div className="cr-section-header">
              <h2>Elegir Jugador</h2>
              <p>Nombra al futbolista que cumpla mas criterios de los elegidos.</p>
            </div>

            <div className="cr-clubs-bar">
              {chosenItems.map((c, i) => (
                <div key={i} className={`cr-club-tag cr-tag-${c.type}`}>{getCriteriaLabel(c)}</div>
              ))}
            </div>

            <div className="cr-turn-banner">
              {botThinking ? (
                <>
                  <div className="cr-dots"><span /><span /><span /></div>
                  <span>{getBotDisplayName(turn)} buscando jugador...</span>
                </>
              ) : turn === 0 ? (
                <span>Tu turno: busca un jugador</span>
              ) : null}
            </div>

            <SpeechBubble />
            <TimerBar />

            {chosenPlayers.length > 0 && (
              <div className="cr-choices">
                {chosenPlayers.map((c, i) => (
                  <div key={i} className="cr-choice-card cr-slide-in" style={{ animationDelay: `${i * 0.15}s` }}>
                    <div className={`cr-choice-avatar ${c.playerIdx === 0 ? 'user' : 'bot'}`} style={getAvatarStyle(c.playerIdx)}>
                      {getAvatarContent(c.playerIdx)}
                    </div>
                    <div className="cr-choice-info">
                      <span className="cr-choice-who">{getBotShortName(c.playerIdx)}</span>
                      <span className="cr-choice-name">{c.name}</span>
                    </div>
                    <div className="cr-choice-pts">+{c.points}</div>
                  </div>
                ))}
              </div>
            )}

            {isUserTurn && (
              <div className="cr-search-area">
                <input
                  type="text"
                  className="cr-search-input"
                  placeholder="Buscar jugador (min. 3 letras)..."
                  value={searchInput}
                  onChange={e => setSearchInput(latinize(e.target.value.toLowerCase()))}
                  autoFocus
                />
                {isSearching && <div className="cr-searching">Buscando...</div>}
                {searchInput.length >= 3 && searchResults.length > 0 && (
                  <div className="cr-select-wrap">
                    <Select
                      options={formatOptions(searchResults)}
                      onChange={handlePlayerPick}
                      menuIsOpen
                      isSearchable={false}
                      classNamePrefix="crs"
                      placeholder="Seleccionar jugador..."
                      components={{ DropdownIndicator: () => null, IndicatorSeparator: () => null }}
                    />
                  </div>
                )}
                {searchInput.length >= 3 && searchResults.length === 0 && !isSearching && (
                  <div className="cr-no-results">No se encontraron jugadores que cumplan los criterios</div>
                )}
              </div>
            )}
          </section>
        )}

        {/* =============== PHASE: RESULTS =============== */}
        {phase === 'results' && (
          <section className="cr-section cr-fade-in">
            <div className="cr-section-header">
              <h2>Resultados - Ronda {round}</h2>
              {winner !== null && (
                <div className="cr-winner-alert">
                  {winner === 0 ? '¡Ganaste el juego!' : `${getBotDisplayName(winner)} gano el juego`} 🏆
                </div>
              )}
            </div>

            <div className="cr-clubs-bar recap">
              {chosenItems.map((c, i) => (
                <div key={i} className={`cr-club-tag cr-tag-${c.type}`}>{getCriteriaLabel(c)}</div>
              ))}
            </div>

            <div className="cr-results-list">
              {sortedResults.map((r, i) => (
                <div
                  key={i}
                  className={`cr-result-row ${i === 0 ? 'winner' : ''} ${animatingResult >= i ? 'visible' : ''}`}
                  style={{ transitionDelay: `${i * 0.15}s` }}
                >
                  <div className="cr-result-pos">
                    {i === 0 ? <span className="cr-trophy">1</span> : <span>{i + 1}</span>}
                  </div>
                  <div className={`cr-result-avatar ${r.playerIdx === 0 ? 'user' : 'bot'}`} style={getAvatarStyle(r.playerIdx)}>
                    {getAvatarContent(r.playerIdx)}
                  </div>
                  <div className="cr-result-info">
                    <span className="cr-result-who">{getBotShortName(r.playerIdx)}</span>
                    <span className="cr-result-player">{r.name}</span>
                    {r.matchingCriteria && r.matchingCriteria.length > 0 && (
                      <div className="cr-result-clubs">
                        {r.matchingCriteria.map((m, ci) => {
                          const label = typeof m === 'string' ? m : (
                            m.type === 'club'
                              ? Object.entries(clubMapping).find(([, v]) => v === m.value)?.[0] || m.value
                              : m.type === 'achievement'
                              ? ACHIEVEMENT_LABELS[m.value] || m.value
                              : m.value
                          );
                          return <span key={ci} className="cr-mini-tag">{label}</span>;
                        })}
                      </div>
                    )}
                  </div>
                  <div className="cr-result-points">+{r.points}</div>
                </div>
              ))}
            </div>

            {/* Bot reactions */}
            <div className="cr-bot-reactions">
              {resultReactions.map(({ botIdx, text }) => {
                const bot = BOT_PERSONALITIES[botIdx - 1];
                return (
                  <div key={botIdx} className="cr-reaction-bubble">
                    <span className="cr-reaction-flag">{bot.flag}</span>
                    <span className="cr-reaction-text">{text}</span>
                  </div>
                );
              })}
            </div>

            <div className="cr-leaderboard">
              <h3>Tabla General — Meta: {pointsGoal} pts</h3>
              {rankedScores.map((s, i) => (
                <div key={s.idx} className={`cr-leader-row ${i === 0 ? 'first' : ''}`}>
                  <span className="cr-leader-pos">{i + 1}</span>
                  <span className={`cr-leader-name ${s.idx === 0 ? 'user-name' : ''}`}>{s.name}</span>
                  <div className="cr-leader-bar-wrap">
                    <div className="cr-leader-bar" style={{ width: `${Math.min(100, (s.score / pointsGoal) * 100)}%` }} />
                  </div>
                  <span className="cr-leader-score">{s.score} pts</span>
                </div>
              ))}
            </div>

            <div className="cr-best-players cr-fade-in">
              <h3>Top 5 — Mejores jugadores para esta combinacion</h3>
              {loadingBest ? (
                <p className="cr-best-loading">Cargando...</p>
              ) : (
                <div className="cr-best-list">
                  {bestPlayers.map((p, i) => (
                    <div key={i} className="cr-best-row">
                      <span className="cr-best-pos">{i + 1}</span>
                      <div className="cr-best-info">
                        {p.img_pais && <img src={p.img_pais} alt="" className="cr-best-flag" />}
                        <span className="cr-best-name">{p.nombre}</span>
                      </div>
                      <span className="cr-best-score">{p.matchingCriteriaCount}/{ITEMS_PER_ROUND}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="cr-next-btn" onClick={startNextRound}>
              {winner !== null ? 'Ver ganador' : 'Siguiente Ronda'}
            </button>
          </section>
        )}

        {/* =============== PHASE: GAMEOVER =============== */}
        {phase === 'gameover' && (
          <section className="cr-section cr-fade-in cr-gameover">
            <div className="cr-gameover-trophy">🏆</div>
            <h2 className="cr-gameover-title">
              {winner === 0 ? '¡Ganaste!' : `${getBotDisplayName(winner)} gano`}
            </h2>
            <p className="cr-gameover-sub">con {scores[winner]} puntos</p>

            <div className="cr-leaderboard cr-gameover-board">
              <h3>Resultado Final</h3>
              {rankedScores.map((s, i) => (
                <div key={s.idx} className={`cr-leader-row ${i === 0 ? 'first' : ''}`}>
                  <span className="cr-leader-pos">{i + 1}</span>
                  <span className={`cr-leader-name ${s.idx === 0 ? 'user-name' : ''}`}>{s.name}</span>
                  <span className="cr-leader-score">{s.score} pts</span>
                </div>
              ))}
            </div>

            <button className="cr-next-btn" onClick={resetGame}>
              Jugar de nuevo
            </button>
          </section>
        )}
      </main>
    </div>
  );
};

export default ClubRacePage;
